/**
 * Copyright (c) 2019-Present Syncari - All rights reserved.
 */
import cx from 'classnames';
import * as React from 'react';

import type { CollapsibleProps } from '../../types';
import './Collapsible.less';

const ChevronIcon = () => (
  <svg className="collapsible__chevron" viewBox="0 0 12 12" width="12" height="12" aria-hidden="true">
    <path
      d="M3 4.5 6 7.5 9 4.5"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Collapsible = ({
  title,
  defaultOpen = true,
  open: controlledOpen,
  onOpenChange,
  divider = false,
  lazy = false,
  className,
  children,
}: CollapsibleProps) => {
  const isControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
  const isOpen = isControlled ? !!controlledOpen : internalOpen;
  // Track whether the panel has ever been opened — used by lazy mode.
  const hasOpenedRef = React.useRef(isOpen);

  const bodyRef = React.useRef<HTMLDivElement>(null);
  // Skip the animation on the very first render — just snap to the initial state.
  const isFirstRender = React.useRef(true);

  React.useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;

    if (isFirstRender.current) {
      isFirstRender.current = false;
      // Snap to initial state without transition.
      body.style.height = isOpen ? 'auto' : '0px';
      return;
    }

    if (isOpen) {
      // Opening: 0 → measured height → auto (so content can resize freely).
      body.style.height = `${body.scrollHeight}px`;
      const onEnd = () => {
        body.style.height = 'auto';
      };
      body.addEventListener('transitionend', onEnd, { once: true });
    } else {
      // Closing: pin at current rendered height first, then animate to 0.
      // The double rAF ensures the browser has painted the pinned height
      // before the transition to 0 starts.
      body.style.height = `${body.scrollHeight}px`;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          body.style.height = '0px';
        });
      });
    }
  }, [isOpen]);

  if (isOpen) hasOpenedRef.current = true;

  const toggle = () => {
    const next = !isOpen;
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };

  return (
    <div className={cx('collapsible', { 'collapsible--open': isOpen }, className)}>
      <button type="button" className="collapsible__trigger" aria-expanded={isOpen} onClick={toggle}>
        <ChevronIcon />
        <span className="collapsible__title">{title}</span>
        <span className="collapsible__rule" aria-hidden="true" />
      </button>
      <div ref={bodyRef} className="collapsible__body" aria-hidden={!isOpen}>
        <div className={cx('collapsible__content', { 'collapsible__content--divider': divider })}>
          {lazy && !hasOpenedRef.current ? null : children}
        </div>
      </div>
    </div>
  );
};

Collapsible.displayName = 'Collapsible';

export default Collapsible;
